<script>
import TableProps from '@/Components/Table/Mixins/TableProps'

/**
 * A reusable table for displaying ordered lists of data
 *
 *  Example use
 *  Any :actions will be added to that list that pops up when you click the button. When you click it, an event of the
 *  same name will be emitted for you to catch.   Row has a base component but the idea is you override it for
 *  whatever the needs of the page you're working on are.  I did exactly this on packages/Index.vue if you
 *  want an example (edited)
 *
 * <data-table :items="arrayOfItems" :headers="arrayOfHeaders" :actions="['edit', 'delete', 'catfood', 'potato']">
 *     <template v-slot:rows="slotRows">
 *         <row v-slot:rows="slotRows"
 *              v-bind="slotRows"
 *              @edit="editSomething(slotRows.item)"
 *              @delete="deleteSomething(slotRows.item)"
 *              @catfood="doACatfood(slotRows.item)"
 *              @potato="potatoHappened(slotRows.item)"
 *         />
 *    </template>
 * </data-table>
 *
 * ********
 * Hiding the select all box
 * ********
 * set :selectable prop to false to hide the "select all" checkbox
 *
 */
export default {
  mixins: [TableProps],
  components: {},

  props: {
    /**
     * An array of items we want to display in this table
     */
    items: {
      type: Array,
      default() {
        return []
      }
    }
  },

  data() {
    return {
      allSelected: false,

      /**
       * Key value list of items that have been selected. Key is the table index of the item, Value is either
       * true or false depending on checkbox status. To be considered selected, items must be in this list
       * and have value = true
       */
      selectedIndexes: {}
    }
  },

  watch: {
    /**
     * Toggles all items on or off when header checkbox is clicked
     */
    allSelected() {
      this.toggleSelectedItems(this.allSelected)
      this.computeAndEmitSelectedItems()
    }
  },

  methods: {
    /**
     * Emits a list of selected items to the v-model variable connected to this component
     */
    computeAndEmitSelectedItems() {
      // Get a list of selected items
      let list = this.getSelectedItems()

      // Pass the list of selected items to the v-model variable linked to this class
      this.$emit('update:modelValue', list)
    },

    /**
     * Returns a list of selected items in the table
     * @returns array list of selected items
     */
    getSelectedItems() {
      const retval = []
      Object.entries(this.selectedIndexes).map((item) => {
        // Left side of entry is the index, right side is the checked state (true/false)
        const [index, checked] = item

        if (checked === true) {
          // Return a shallow copy of the selected item so that we aren't passing around ES6 Proxy objects
          retval.push({ ...this.items[index] })
        }
      })

      return retval
    },

    /**
     * Toggles all items as selected or not selected
     * @param toggle selects all items if true. Deselects all items if false
     */
    toggleSelectedItems(toggle = true) {
      Object.keys(this.items).map((index) => {
        this.selectedIndexes[index] = toggle
      })
    },

    /**
     * Called when a child row component emits a selected event
     * @param index the index of the table that was selected
     * @param state true if checked, false if not checked
     */
    rowSelected(index, state) {
      this.selectedIndexes[index] = state
      this.computeAndEmitSelectedItems()
    }
  }
}
</script>

<template>
  <!--    <div class="table-shadow relative overflow-hidden">-->
  <div class="table-container w-full overflow-x-auto whitespace-nowrap relative">
    <table class="w-full">
      <thead>
        <tr class="header-row">
          <th
            v-if="selectable"
            scope="col"
            class="header-checkbox"
          >
            <input
              v-if="selectable"
              type="checkbox"
              class="form-checkbox"
              v-model="allSelected"
            />
          </th>

          <th
            v-for="header in headers"
            scope="col"
          >
            {{ header }}
          </th>
        </tr>
      </thead>

      <tbody class="table-rows">
        <slot
          name="rows"
          v-if="items.length > 0"
          v-for="(item, index) in items"
          :item="item"
          :index="index"
          :actions="actions"
          @selected="rowSelected"
          :selected="allSelected"
        />
      </tbody>
    </table>
  </div>
  <!--    </div>-->
</template>
